/**
 * Date utility functions to handle timezone-safe date operations
 */

/**
 * Formats a Date object to YYYY-MM-DD string in local timezone
 * This prevents the timezone offset issues that occur with toISOString()
 * 
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format in local timezone
 */
export const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Gets today's date in YYYY-MM-DD format in local timezone
 * 
 * @returns Today's date string in YYYY-MM-DD format
 */
export const getTodayLocal = (): string => {
  return formatDateToLocal(new Date());
};

/**
 * Parses a YYYY-MM-DD date string to a Date object at midnight in local timezone
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing midnight of the given date in local timezone
 */
export const parseDateLocal = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Checks if two dates represent the same day (ignoring time)
 * 
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if both dates represent the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

/**
 * Checks if a date is today
 * 
 * @param date - The date to check
 * @returns True if the date is today
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, new Date());
};
/**
 * `18:00:00` / `18:00` → `18:00` — a .NET `TimeSpan` szerver-alakját az időválasztó által
 * írt „HH:mm” alakra hozza.
 *
 * MIÉRT KELL [BL-2, 2026-09-13]: a szerver „09:00:00”-t ad, az időválasztó viszont
 * `toTimeString().slice(0, 5)`-tel „09:00”-t ír vissza. Ha a betöltött alapvonal a NYERS
 * szerver-alakot őrzi, a mentetlen-változás védelem az eredeti idő VISSZAÁLLÍTÁSA után is
 * piszkosnak látja az űrlapot (mérve: az elérhetőség-szerkesztőn 09:00 → 10:00 → 09:00).
 * Ezért EGY helyen áll ez az átalakítás, és minden idő-űrlap ezt használja.
 *
 * ⚠️ A MÁSODPERC ELVESZIK — és ez SZÁNDÉKOS [N-6, 2026-09-14]. A nem-nulla másodperc (`09:00:30`)
 * ebből `09:00` lesz, tehát egy ilyen érték betöltés+mentés után 09:00-ra rövidülne. MIÉRT vállalt:
 * (a) az idő-választó nem tud másodpercet megjeleníteni, tehát a mező sosem tudná visszaadni;
 * (b) a klub adatában nincs nem-egész-perces időpont (MÉRVE a dev/E2E adatbázison: 0 db), a
 * felület minden időt perces léptékkel ír; (c) a rövidítés a betöltőben történik, tehát az ŰRLAP és
 * az ALAPVONAL UGYANEZT az értéket kapja → a mentetlen-változás védelem nem ad hamis riasztást.
 *
 * ⚠️ ELLENTMONDÁSNAK LÁTSZIK, DE NEM AZ: a `formDirty.ts` `WHOLE_MINUTE_TIME` szabálya csak a
 * `:00` végű másodpercet vágja le, a nem-nullát MEGTARTJA (`isFormDirty({t:'09:00:30'},{t:'09:00'})`
 * → PISZKOS). A két fél szándékosan más irányba hibázik biztonságosan: az ÖSSZEHASONLÍTÁS nem
 * titkolhat el eltérést (inkább kérdezzen), a BETÖLTŐ pedig a mező által megjeleníthető alakra hoz
 * (inkább ne kérdezzen olyanra, amit a felhasználó nem is tud átírni). A két szabály a VALÓBAN
 * előforduló adaton (egész perc) azonos eredményt ad.
 *
 * @param time Az idő szöveges alakja (`HH:mm`, `HH:mm:ss`, `H:mm`)
 * @returns `HH:mm` alak; értelmezhetetlen bemenetet változatlanul ad vissza
 */
export const toHourMinute = (time: string): string => {
  if (!time) return time;
  const [hour, minute] = time.split(':');
  return hour !== undefined && minute !== undefined ? `${hour.padStart(2, '0')}:${minute}` : time;
};

/* ------------------------------------------------------------------------------------------------
 * SZERVER-IDŐBÉLYEGEK ÉRTELMEZÉSE ÉS FORMÁZÁSA — [Web UX 1. csomag (4.3, 5.1 részben), 2026-10-03]
 *
 * MI VOLT A BAJ: a szerver az időbélyegeket UTC-ben menti (`DateTime.UtcNow`, pl.
 * `SosAccessService.cs:344` `AccessedAt = mostUtc`), az EF visszaolvasáskor `Kind=Unspecified`-et ad,
 * ezért a JSON-ban NINCS „Z” (élő API-n MÉRVE 2026-10-03 15:52 UTC-kor: SOS-napló `accessedAt`,
 * jelenlét `recordedDate`, edzői megjegyzés, hírek, elérhetőség, kifizetés, jegyzetek,
 * magánóra-igénylés `createdDate`/`handledDate`, felhasználó `createdDate` — mind „Z” nélkül; CSAK
 * az üzenetek `sentDate`-je jön „Z”-vel, mert ott a szerver `SpecifyKind(…, Utc)`-t ad,
 * `MessagesController.cs:179`). A böngésző a zóna nélküli szöveget HELYI időnek veszi →
 * a felület 1–2 órával korábbi időt mutatott (SOS napló mérve: 13:15 a valódi 15:15 helyett),
 * 00:00–02:00 között pedig az előző NAPOT.
 *
 * A `parseServerDateTime` SZABÁLYAI (sorrendben):
 *   1. `null` / `undefined` / üres szöveg → `null` (a hívó dönt a „-” / „Nincs adat” kiírásról);
 *   2. ha a szöveg MÁR hordoz időzónát (`Z`, `±hh:mm` vagy `±hhmm` a végén) → VÁLTOZATLANUL
 *      értelmezi (nem fűz hozzá még egy „Z”-t — a régi `AuditLogViewer` minta,
 *      `endsWith('Z') ? s : s + 'Z'`, egy `+02:00` végű szövegből érvénytelen dátumot csinált);
 *   3. DÁTUM-CSAK (`yyyy-MM-dd`) → HELYI éjfél (`parseDateLocal`), NEM UTC — a
 *      `new Date('yyyy-MM-dd')` UTC-éjfelet ad (memória: `utc-vs-local-day-bug-class`);
 *   4. zóna nélküli dátum-idő (`yyyy-MM-ddTHH:mm[:ss[.f…]]`, a .NET 7 tizedest is ír) → UTC-nek
 *      veszi („Z”-t fűz hozzá);
 *   5. bármi más / érvénytelen → `null` (nem dob).
 *
 * ⚠️ DÁTUM-ÉRTÉKŰ MEZŐRE NEM: egy éjfélre állított `DateTime` mezőt (`"…T00:00:00"`, pl. az esemény
 * `eventDate`-je) TILOS ezzel (vagy a ráépülő `formatServer*` formázókkal) kiírni — a 4. szabály
 * UTC-nek venné, és budapesti 01:00/02:00-t adna. A `formatServerDate` csak dátum-csak
 * (`yyyy-MM-dd`) szövegre való; dátum-értékű mezőhöz a meglévő dátum-segédek (`parseDateLocal`,
 * `formatDateToLocal`) kellenek, a szöveg dátum-részével.
 *
 * A FORMÁZÁS EXPLICIT `Europe/Budapest` zónával történik (`Intl.DateTimeFormat(..., { timeZone })`
 * részeiből rakjuk össze a pontos alakot), NEM a gép időzónájával: a klub minden gépe Budapesten
 * van, de így egy nem budapesti gépen (vagy CI-n) is a klub-időt mutatja, és a teszt sem csak a gép
 * időzónája miatt zöld. A DÁTUM-CSAK bemenetet a formázók közvetlenül a szövegből írják ki (nincs
 * zóna-tolás), így `formatServerDate('2026-10-02')` bármely gép-időzónában `2026. 10. 02.`.
 * ---------------------------------------------------------------------------------------------- */

/** A klub időzónája — minden szerver-időbélyeg ebben jelenik meg. */
export const CLUB_TIME_ZONE = 'Europe/Budapest';

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const ZONED_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * A JS `Date` csak 3 tizedest (ezredmásodperc) értelmez megbízhatóan; a .NET 7-et ír
 * (`.1234567`). A felesleget levágjuk (kerekítés nélkül — megjelenítésnél a perc a lényeg).
 * A `±hhmm` (kettőspont nélküli) eltolást `±hh:mm`-re hozzuk, mert azt minden motor érti.
 */
const normalizeIsoForDate = (s: string): string =>
  s
    .replace(/(\.\d{3})\d+/, '$1')
    .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');

/**
 * A szerver-időbélyeg bemenete. A `Date` azért megengedett, mert néhány régi típus-deklaráció
 * `Date`-et ír (pl. `EventAttendance.tsx` `recordedDate?: Date`), holott futásidőben a JSON-ból
 * mindig SZÖVEG jön; egy valódi `Date` már időpont, azt változatlanul használjuk.
 */
export type ServerDateInput = string | Date | null | undefined;

/**
 * Szerver-időbélyeg (vagy dátum-csak mező) → `Date`, a fenti 5 szabály szerint.
 * @returns `Date`, vagy `null`, ha a bemenet üres vagy nem értelmezhető (soha nem dob).
 */
export const parseServerDateTime = (s: ServerDateInput): Date | null => {
  if (s === null || s === undefined) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;
  const text = String(s).trim();
  if (!text) return null;

  if (DATE_ONLY_RE.test(text)) {
    if (!dateOnlyParts(text)) return null;
    return parseDateLocal(text);
  }

  let iso: string;
  if (ZONED_DATE_TIME_RE.test(text)) {
    iso = normalizeIsoForDate(text);
  } else if (NAIVE_DATE_TIME_RE.test(text)) {
    iso = normalizeIsoForDate(text) + 'Z';
  } else {
    return null;
  }

  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

interface ClubDateParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

let clubPartsFormatter: Intl.DateTimeFormat | null = null;
const getClubPartsFormatter = (): Intl.DateTimeFormat => {
  if (!clubPartsFormatter) {
    clubPartsFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: CLUB_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  }
  return clubPartsFormatter;
};

/** Egy időpont budapesti naptári részei (2 jegyre töltve). */
const toClubParts = (date: Date): ClubDateParts => {
  const parts: Record<string, string> = {};
  for (const p of getClubPartsFormatter().formatToParts(date)) parts[p.type] = p.value;
  // Néhány motor a h23 ellenére éjfélt „24”-ként adhat — biztosítjuk a 00-t.
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour,
    minute: parts.minute,
    second: parts.second,
  };
};

/** Dátum-csak szövegből a nap részei — zóna-konverzió NÉLKÜL (a nap maga); nem létező nap → null. */
function dateOnlyParts(text: string): { year: string; month: string; day: string } | null {
  const m = DATE_ONLY_RE.exec(text);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
    return null;
  }
  return { year: m[1], month: m[2], day: m[3] };
}

const HU_MONTHS = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
];

export interface ServerDateFormatOptions {
  /** Mit írjon ki üres / értelmezhetetlen bemenetre (alapértelmezés: üres szöveg). */
  fallback?: string;
}

export interface ServerDateTimeFormatOptions extends ServerDateFormatOptions {
  /** Év nélküli rövid alak (`10. 02. 15:15`) — szűk helyre (telefonos kártya). */
  withoutYear?: boolean;
  /** Másodperccel (`2026. 10. 02. 15:15:30`) — pl. az audit-naplóban. */
  withSeconds?: boolean;
}

const isBlank = (s: ServerDateInput): boolean =>
  s === null || s === undefined || (!(s instanceof Date) && !String(s).trim());

/** Dátum-csak szöveg részei, vagy null (egy `Date` bemenet sosem dátum-csak). */
const dateOnlyOf = (s: ServerDateInput): { year: string; month: string; day: string } | null =>
  typeof s === 'string' ? dateOnlyParts(s.trim()) : null;

/**
 * Szerver-időbélyeg → `2026. 10. 02. 15:15` (a magyar szabvány alak, budapesti idő).
 * Dátum-csak bemenetre csak a dátumot írja (`2026. 10. 02.`) — kitalált 00:00 nélkül.
 */
export const formatServerDateTime = (
  s: ServerDateInput,
  options: ServerDateTimeFormatOptions = {},
): string => {
  const { fallback = '', withoutYear = false, withSeconds = false } = options;
  if (isBlank(s)) return fallback;
  const dOnly = dateOnlyOf(s);
  if (dOnly) {
    return withoutYear ? `${dOnly.month}. ${dOnly.day}.` : `${dOnly.year}. ${dOnly.month}. ${dOnly.day}.`;
  }
  const date = parseServerDateTime(s);
  if (!date) return fallback;
  const p = toClubParts(date);
  const time = withSeconds ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
  return withoutYear ? `${p.month}. ${p.day}. ${time}` : `${p.year}. ${p.month}. ${p.day}. ${time}`;
};

/** Szerver-időbélyeg vagy dátum-csak mező → `2026. 10. 02.` (a budapesti NAP). */
export const formatServerDate = (
  s: ServerDateInput,
  options: ServerDateFormatOptions = {},
): string => {
  const { fallback = '' } = options;
  if (isBlank(s)) return fallback;
  const dOnly = dateOnlyOf(s);
  if (dOnly) return `${dOnly.year}. ${dOnly.month}. ${dOnly.day}.`;
  const date = parseServerDateTime(s);
  if (!date) return fallback;
  const p = toClubParts(date);
  return `${p.year}. ${p.month}. ${p.day}.`;
};

/** Szerver-időbélyeg → `15:15` (budapesti idő). Dátum-csak bemenetre a `fallback`. */
export const formatServerTime = (
  s: ServerDateInput,
  options: ServerDateFormatOptions = {},
): string => {
  const { fallback = '' } = options;
  if (isBlank(s)) return fallback;
  if (dateOnlyOf(s)) return fallback;
  const date = parseServerDateTime(s);
  if (!date) return fallback;
  const p = toClubParts(date);
  return `${p.hour}:${p.minute}`;
};

export interface ServerDateLongFormatOptions extends ServerDateFormatOptions {
  /** Az idővel együtt (`2026. október 2. 15:15`). */
  withTime?: boolean;
}

/**
 * Hosszú, olvasható alak a hírekhez / igénylésekhez: `2026. október 2.` (vagy
 * `2026. október 2. 15:15`). A hónapnév kézi táblából jön (nem a futtató ICU-adatából), így
 * minden környezetben ugyanaz. Dátum-csak bemenet → a nap maga, idő nélkül.
 */
export const formatServerDateLong = (
  s: ServerDateInput,
  options: ServerDateLongFormatOptions = {},
): string => {
  const { fallback = '', withTime = false } = options;
  if (isBlank(s)) return fallback;
  const dOnly = dateOnlyOf(s);
  if (dOnly) return `${dOnly.year}. ${HU_MONTHS[Number(dOnly.month) - 1]} ${Number(dOnly.day)}.`;
  const date = parseServerDateTime(s);
  if (!date) return fallback;
  const p = toClubParts(date);
  const base = `${p.year}. ${HU_MONTHS[Number(p.month) - 1]} ${Number(p.day)}.`;
  return withTime ? `${base} ${p.hour}:${p.minute}` : base;
};

const HU_MONTHS_SHORT = [
  'jan.', 'febr.', 'márc.', 'ápr.', 'máj.', 'jún.',
  'júl.', 'aug.', 'szept.', 'okt.', 'nov.', 'dec.',
];

/**
 * DÁTUM-CSAK IDŐSZAK rövid magyar alakja [Web UX 3. csomag (C), 2026-10-04] — pl. a kifizetés
 * megerősítő ablakában: „Kiss Dániel · 2026. szept. 1.–30. · 33 000 Ft".
 *   ugyanaz a hónap:  `2026. szept. 1.–30.`
 *   más hónap:        `2026. szept. 15.–okt. 14.`
 *   más év:           `2025. dec. 15.–2026. jan. 14.`
 *   egy nap:          `2026. szept. 1.`
 *
 * A bemenet DÁTUM-CSAK érték: `yyyy-MM-dd`, vagy a .NET `DateTime` „Z" nélküli, éjféli alakja
 * (`2026-09-01T00:00:00` — a kifizetés `PeriodStart`/`PeriodEnd` mezője ilyen,
 * `backend/PinyoApi/DTOs/CoachPaymentDto.cs:13-14`). Csak az ELSŐ 10 karakter számít: a NAP maga,
 * időzóna-konverzió NÉLKÜL — ugyanaz a szabály, mint a `formatServerDate` dátum-csak ágán.
 * ⚠️ `new Date('yyyy-MM-dd')` (UTC éjfél) itt SZÁNDÉKOSAN nincs; a hónap-rövidítés kézi táblából
 * jön (nem a futtató ICU-adatából), tehát minden környezetben ugyanaz.
 * Értelmezhetetlen bemenet → `fallback` (alapból üres szöveg).
 *
 * FELTEVÉS [1. validátor-kör M2, 2026-10-04]: a szerver ma „Z" NÉLKÜL küldi ezeket a mezőket (élő
 * API-n mérve, a fenti DTO). Ha egyszer ZÓNÁS alakot küldene (`…Z` vagy `…+02:00`), az első 10
 * karakter az UTC- (ill. a megadott zónájú) napot adná, ami Budapesten a következő nap is lehet
 * (`2026-08-31T22:00:00Z` = szept. 1. 00:00 Budapesten). Ezért a zónás alakot a közös
 * `parseServerDateTime` + budapesti nap (`toClubParts`) szerint bontjuk — ugyanaz a szabály, mint a
 * `formatServerDate`-é. Őr: `dateUtils.dateOnlyRange.test.ts` („zónás alak" eset).
 */
export const formatDateOnlyRange = (
  start: ServerDateInput,
  end: ServerDateInput,
  options: ServerDateFormatOptions = {},
): string => {
  const { fallback = '' } = options;
  const partsOf = (v: ServerDateInput) => {
    if (typeof v !== 'string') return null;
    const text = v.trim();
    if (ZONED_DATE_TIME_RE.test(text)) {
      const d = parseServerDateTime(text);
      if (!d) return null;
      const p = toClubParts(d);
      return { year: p.year, month: p.month, day: p.day };
    }
    return dateOnlyParts(text.slice(0, 10));
  };
  const s = partsOf(start);
  const e = partsOf(end);
  if (!s || !e) return fallback;
  const mon = (p: { month: string }) => HU_MONTHS_SHORT[Number(p.month) - 1];
  const day = (p: { day: string }) => Number(p.day);
  if (s.year !== e.year) return `${s.year}. ${mon(s)} ${day(s)}.–${e.year}. ${mon(e)} ${day(e)}.`;
  if (s.month !== e.month) return `${s.year}. ${mon(s)} ${day(s)}.–${mon(e)} ${day(e)}.`;
  if (s.day === e.day) return `${s.year}. ${mon(s)} ${day(s)}.`;
  return `${s.year}. ${mon(s)} ${day(s)}.–${day(e)}.`;
};
